import {Tab} from '@gwc/core'
import {isEqual} from '@logistics-frontend/utils'
import clsx from 'clsx'
import {Fragment, useMemo, useRef} from 'react'

import {Tooltip} from '../tooltip'
import {TabItem, TabsProps, Value} from './Tabs'
import {classes} from './Tabs.classes'

export const useCachedState = <T,>() => {
  const memo = useRef<T | null>(null)

  return {
    getValue: (nextValue: T): T => {
      if (!isEqual(nextValue, memo.current)) {
        memo.current = nextValue
      }
      return memo.current || nextValue
    },
  }
}

// Потому что у тайпскрипта есть бага: на юнионах списков он не видит методы списков, на месте делаем такой каст =/
// number[] | string[] ===> (number | string)[]
type UnionArraySwap<T> = Array<T extends Array<infer U> ? U : never>

export const useTabs = <T extends Value>(itemsProp: TabItem<T>[], disabled: TabsProps<T>['disabled'] = false) => {
  const {getValue: getItems} = useCachedState<Array<TabItem<T>>>()

  const items = getItems(itemsProp)

  // Хешмапа для удобного определения состояния дизейбленности итема: true/false - включен/выключен, string - выключен с тултипом с содержанием строки
  const disabledMap: Partial<Record<T, string | boolean>> = useMemo(
    () =>
      typeof disabled === 'boolean'
        ? items.reduce((acc: Partial<Record<T, boolean>>, {value}) => {
            acc[value] = disabled
            return acc
          }, {})
        : (disabled as UnionArraySwap<typeof disabled>).reduce(
            (acc: Partial<Record<T, string | boolean>>, item: typeof disabled[number]) => {
              if (typeof item === 'object') {
                acc[item.value] = item.reason
              } else {
                acc[item] = true
              }
              return acc
            },
            {},
          ),
    [disabled, items],
  )

  return useMemo(
    () =>
      items.map(({label, value}) => {
        const disabledValue = disabledMap[value] ?? false

        return (
          <Fragment key={value.toString()}>
            {typeof disabledValue === 'string' ? (
              <Tooltip title={disabledValue} invert>
                <Tab className={classes.disabled} value={value} disabled>
                  {label}
                </Tab>
              </Tooltip>
            ) : (
              <Tab className={clsx({[classes.disabled]: disabledValue})} disabled={disabledValue} value={value}>
                {label}
              </Tab>
            )}
          </Fragment>
        )
      }),
    [items, disabledMap],
  )
}
